import * as React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsxRuntime classic */
/* @jsx mdx */
import DefaultLayout from "/home/runner/work/design/design/node_modules/@primer/gatsby-theme-doctocat/src/components/layout.js";
import Code from '@primer/gatsby-theme-doctocat/src/components/code';
export const _frontmatter = {};
const makeShortcode = name => function MDXDefaultShortcode(props) {
  console.warn("Component " + name + " was not imported, exported, or provided by MDXProvider as global scope");
  return <div {...props} />;
};
const DoDontContainer = makeShortcode("DoDontContainer");
const Do = makeShortcode("Do");
const Dont = makeShortcode("Dont");
const Note = makeShortcode("Note");
const layoutProps = {
  _frontmatter
};
const MDXLayout = DefaultLayout;
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">



    <p>{`Your first port of call for general content guidelines should be `}<a parentName="p" {...{
        "href": "https://github.com/github/brand/blob/main/content.md"
      }}>{`GitHub’s content guide`}</a>{` (link only accessible to GitHub staff), followed by the `}<a parentName="p" {...{
        "href": "https://docs.microsoft.com/en-us/style-guide/welcome/"
      }}>{`Microsoft Style Guide`}</a>{`.`}</p>
    <h2>{`UI content principles`}</h2>
    <p>{`Keep the following principles in mind when creating UI content for GitHub.`}</p>
    <h3>{`1. Keep it clear`}</h3>
    <p>{`Aim for a seventh-grade or below reading level. This means you should write text that is straightforward, without trying to be creative with words.`}</p>
    <p>{`There are some useful tools you can use to test your content for simplicity and reading level:`}</p>
    <ul>
      <li parentName="ul"><a parentName="li" {...{
          "href": "https://www.grammarly.com/"
        }}>{`Grammarly`}</a></li>
      <li parentName="ul"><a parentName="li" {...{
          "href": "http://www.hemingwayapp.com/"
        }}>{`Hemingway`}</a></li>
      <li parentName="ul"><a parentName="li" {...{
          "href": "https://www.readabilityformulas.com/free-readability-formula-tests.php"
        }}>{`Automatic Readability Checker`}</a></li>
    </ul>
    <h3>{`2. Keep it consistent`}</h3>
    <p>{`Refer to the same actions, sections, labels, landing pages, and so on, the same way across the product. But don’t sacrifice clarity over consistency.`}</p>
    <h2>{`Voice and tone`}</h2>
    <p>{`GitHub’s voice is:`}</p>
    <ul>
      <li parentName="ul">{`Clear but not cold`}</li>
      <li parentName="ul">{`Conversational but not jargon-y`}</li>
      <li parentName="ul">{`Inclusive but not disingenuous`}</li>
      <li parentName="ul">{`Helpful but not overly-prescriptive`}</li>
    </ul>
    <p>{`Tone is how you express personality and mood depending on the situation. GitHub’s tone is almost always `}<strong parentName="p">{`informal and positive`}</strong>{`, with adjustments when needed depending on the channel, audience, and emotional state of the audience.`}</p>
    <p>{`You should consider the context of when a user will read something, keeping in mind that:`}</p>
    <ul>
      <li parentName="ul">{`Humor isn’t welcome in all situations, for example: in errors, when waiting, or when something fails.`}</li>
      <li parentName="ul">{`Error messages should be understandable by humans.`}</li>
    </ul>
    <p>{`Read more about GitHub’s `}<a parentName="p" {...{
        "href": "https://github.com/github/brand/blob/main/content.md#voice-and-tone"
      }}>{`voice and tone in the content guide`}</a>{` (link only accessible to GitHub staff).`}</p>
    <h2>{`Top 10 rules`}</h2>
    <p>{`If you’re in a hurry, these are the most important content guidelines to keep in mind, in no particular order.`}</p>
    <ul>
      <li parentName="ul">{`Write in plain English, don’t sound like a robot.`}</li>
      <li parentName="ul">{`Be brief, remove unnecessary words like adjectives and adverbs.`}</li>
      <li parentName="ul">{`Use active voice.`}</li>
      <li parentName="ul">{`Use sentence case, and when in doubt, don’t capitalize.`}</li>
      <li parentName="ul">{`Always capitalize GitHub correctly.`}</li>
      <li parentName="ul">{`Avoid gendered language.`}</li>
      <li parentName="ul">{`Do not use slang or culturally-specific references.`}</li>
      <li parentName="ul">{`Do not use “here” or “click here” in calls to action.`}</li>
      <li parentName="ul">{`Be very thoughtful when introducing humor to the interface.`}</li>
      <li parentName="ul">{`Have someone else proofread your text.`}</li>
    </ul>
    <h2>{`Content guidelines`}</h2>
    <h3>{`Acronyms and abbreviations`}</h3>
    <p>{`Use acronyms when they are more widely used than the spelled out term.`}</p>
    <p>{`“Pull request” should never be abbreviated. It’s always lowercase unless it’s starting a sentence.`}</p>
    {/* TODO: Where do we abbreviate? Months */}
    {/*
     ### Alternative text
     TODO: In which cases should the UI include alternative text that doesn’t come for free with Primer.
     Do not replace text with emojis.
     */}
    <h3>{`Dates and time`}</h3>
    <p>{`When referring to times, use am and pm, and not any other variation (a.m., A.M., AM).`}</p>
    {/* TODO: Dates. */}
    <h3>{`Emojis`}</h3>
    <p>{`Avoid using emojis, but when you do:`}</p>
    <ul>
      <li parentName="ul">{`Use them only at the end of a sentence`}</li>
      <li parentName="ul">{`Use only well-recognized emojis`}</li>
      <li parentName="ul">{`Do not repeat emojis`}</li>
      <li parentName="ul">{`Use emojis that will work well in both dark and light modes`}</li>
    </ul>
    {/* See also alternative text. */}
    <h3>{`Error messages`}</h3>
    <p>{`Be friendly and helpful, and avoid jargon. Do not blame the user.`}</p>
    <DoDontContainer mdxType="DoDontContainer">
  <Do indented mdxType="Do">
    <em>Enter a name</em><br />
    <em>Your name must be between 2 and 20 characters</em>
  </Do>
  <Dont indented mdxType="Dont">
    <em>Error 1234567890</em><br />
    <em>You forgot to add your name</em>
  </Dont>
    </DoDontContainer>
    <p>{`Be specific.`}</p>
    <DoDontContainer mdxType="DoDontContainer">
  <Do indented mdxType="Do">
    <em>Enter a credit card number</em>
  </Do>
  <Dont indented mdxType="Dont">
    <em>Field required</em>
  </Dont>
    </DoDontContainer>
    <p>{`In most cases, apologizing won't fix the problem. As a rule, do not apologize too much in any situation in the UI.`}</p>
    <DoDontContainer mdxType="DoDontContainer">
  <Do indented mdxType="Do">
    <em>All checks failed</em>
  </Do>
  <Dont indented mdxType="Dont">
    <em>Sorry, all checks failed</em>
  </Dont>
    </DoDontContainer>
    <p>{`Do not try to be funny or humorous.`}</p>
    <DoDontContainer mdxType="DoDontContainer">
  <Do indented mdxType="Do">
    <em>Some checks failed</em>
  </Do>
  <Dont indented mdxType="Dont">
    <em>Oops, some checks failed</em>
  </Dont>
    </DoDontContainer>
    <h3>{`Feedback`}</h3>
    <p>{`Feedback should be clear and reassuring, using the same terms used by the UI elements that triggered it. Don't try to be funny, and avoid jargon.`}</p>
    <DoDontContainer mdxType="DoDontContainer">
  <Do indented mdxType="Do">
    <em>Issue transfer in progress</em><br /><br />
    Feedback message when transferring an issue, using the same term ("transfer") as the trigger button
  </Do>
  <Dont indented mdxType="Dont">
    <em>Moving the issue</em><br /><br />
    Feedback message that uses a different term ("moving") than the trigger button
  </Dont>
    </DoDontContainer>
    {/* TODO: More confirmation messages. Save, close, dismiss. */}
    <h3>{`Forms`}</h3>
    <p>{`Use sentence case for form titles, labels, and fields.
Do not include colons in form labels.`}</p>
    {/* TODO: Error messages, form validation examples. */}
    <h3>{`Labels and buttons`}</h3>
    <p>{`All labels and button text should be in sentence case, and not include punctuation.`}</p>
    <DoDontContainer mdxType="DoDontContainer">
  <Do indented mdxType="Do">
    <em>Save email preferences</em><br />
    <em>1 linked pull request</em><br />
    <em>12 days ago</em>
  </Do>
  <Dont indented mdxType="Dont">
    <em>Save email preferences.</em><br />
    <em>1 Linked pull request</em><br />
    <em>12 Days ago</em>
  </Dont>
    </DoDontContainer>
    <Note mdxType="Note">
  When a sentence or label starts with a number (for example, “2 references”), the number is the first word, and the
  rest of the sentence should be in lowercase.
    </Note>
    <p>{`Action labels should start with an imperative verb that clearly indicates what to expect.`}</p>
    <DoDontContainer mdxType="DoDontContainer">
  <Do indented mdxType="Do">
    <em>Save preferences</em>
  </Do>
    </DoDontContainer>
    <p>{`Action labels can be shortened to only include an adjective and a noun.`}</p>
    <DoDontContainer mdxType="DoDontContainer">
  <Do indented mdxType="Do">
    <em>New issue</em>
  </Do>
    </DoDontContainer>
    <p>{`Use “sign in” rather than “log in”.`}</p>
    <h3>{`Links`}</h3>
    <p>{`Link text should be meaningful and unique, with as few duplicated references as possible to prevent confusion. Ideally the link itself, or, alternatively, its programmatically determined context, should provide information about where the link will take you, and help users decide whether to follow the link. Examples of programmatically determined context can be aria labels, or text within the same paragraph as the link.`}</p>
    <DoDontContainer stacked mdxType="DoDontContainer">
  <Do indented mdxType="Do">
    <em>You can find out more about our speakers at the <a href="https://githubuniverse.com/" target="_blank">GitHub Universe site</a></em>
  </Do>
    </DoDontContainer>
    <p>{`Set the context of the image link from the text within the same container.`}</p>
    <DoDontContainer stacked mdxType="DoDontContainer">
  <Do indented mdxType="Do">
    <Code className="language-html" mdxType="Code">
      {`<a href="/pricing">
   <img src="pricing.png" alt="">
   Pricing information
</a>`}
    </Code>
  </Do>
    </DoDontContainer>
    <p>{`Never say “here” or “click here”.`}</p>
    <DoDontContainer stacked mdxType="DoDontContainer">
  <Dont indented mdxType="Dont">
    <em>Click here to find out more about our speakers at GitHub Universe.</em>
  </Dont>
    </DoDontContainer>
    {/*
     ### Numbers
     TODO: Commas, truncation, rounding up or down. Do units have a space, KB, MB, etc.?
     */}
    <h3>{`Punctuation`}</h3>
    <p>{`Headings, labels and buttons should not include punctuation. In most cases, exclamation marks are not appropriate — most things aren't `}<em parentName="p">{`that`}</em>{` exciting.`}</p>
    <DoDontContainer mdxType="DoDontContainer">
  <Do indented mdxType="Do">
    <em>No code scanning alerts found</em>
  </Do>
  <Dont indented mdxType="Dont">
    <em>No code scanning alerts found!</em>
  </Dont>
    </DoDontContainer>
    <h3>{`References to UI`}</h3>
    <p>{`When referring to unclickable page names and sections, write them as they appear in the interface in quotation marks.`}</p>
    <DoDontContainer mdxType="DoDontContainer">
  <Do indented mdxType="Do">
    <em>Go to the “Email notifications settings” section.</em>
  </Do>
    </DoDontContainer>
    <p>{`When referring to button or link text that a user should click on, use bold text without quotation marks. Make sure all UI references match the capitalization in the interface. If you’re writing for a mobile app experience, use “tap”, instead of “click”.`}</p>
    <DoDontContainer mdxType="DoDontContainer">
  <Do indented mdxType="Do">
    <em>Click <strong>Save</strong></em>
  </Do>
    </DoDontContainer>
    <p>{`When referring to a folder, use a code tag.`}</p>
    <DoDontContainer mdxType="DoDontContainer">
  <Do indented mdxType="Do">
        <p><em parentName="p">{`Open the `}<inlineCode parentName="em">{`docs`}</inlineCode>{` folder.`}</em></p>
  </Do>
    </DoDontContainer>
    <h3>{`Subject`}</h3>
    <p>{`In most instances, address the user as "you" and items "owned" by the user as "your" or "yours".`}</p>
    <DoDontContainer mdxType="DoDontContainer">
  <Do indented mdxType="Do">
    <em>Update your profile</em>
  </Do>
  <Dont indented mdxType="Dont">
    <em>Update my profile</em>
  </Dont>
    </DoDontContainer>
    <p>{`Some exceptions exist and are usually related to legal language, such as when a user needs to agree to terms, or confirm a destructive action.`}</p>
    <DoDontContainer mdxType="DoDontContainer">
  <Do indented mdxType="Do">
    <em>I have read the Terms of Service</em><br />
    <em>I understand, convert this issue to a discussion</em>
  </Do>
    </DoDontContainer>
    {/*
     ### Text truncation
     TODO: Examples of truncation: dates, large numbers, long file names.
     */}
    <h2>{`What to avoid`}</h2>
    <ul>
      <li parentName="ul">{`Do not say that something is “easy”, “quick”, or that the user “just” needs to do something.`}</li>
      <li parentName="ul">{`Do not capitalize common terms. Only proper nouns and product names should be capitalized.`}</li>
      <li parentName="ul">{`Do not use emoji in UI content, or to replace words.`}</li>
      <li parentName="ul">{`Use the word “and” instead of an ampersand or the “+” sign.`}</li>
      <li parentName="ul">{`Do not use exclamation marks to indicate excitement, most actions aren’t.`}</li>
      <li parentName="ul">{`Do not use the greater than and less than symbols to indicate steps in a flow.`}</li>
      <li parentName="ul">{`Do not use semicolons: they are hard to get right and can be replaced by a period most times.`}</li>
    </ul>
    <h2>{`How to get help`}</h2>
    <p>{`If you need clarification and can’t find an answer in `}<a parentName="p" {...{
        "href": "https://github.com/github/brand/blob/main/content.md"
      }}>{`GitHub’s content guide`}</a>{` (link only accessible to GitHub staff) or the `}<a parentName="p" {...{
        "href": "https://docs.microsoft.com/en-us/style-guide/welcome/"
      }}>{`Microsoft Style Guide`}</a>{`, or would like your UI content to be reviewed, please start a discussion in github/primer and someone in the team will get back to you. If your query is urgent, you can ask directly in the `}<a parentName="p" {...{
        "href": "https://github.slack.com/archives/CSGAVNZ19"
      }}>{`#primer`}</a>{` channel.`}</p>

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      